import {
  type CellEditorOverride,
  type CellEditorOverrideResult,
  getCellValueInCurrentColumn,
} from "@platform/btx-plan-designer";
import { CoverageTypeEnum } from "@platform/types";
import get from "lodash/get";
import {
  accidentTypedAmountFields,
  basicLifeTypedAmountFields,
  cancerTypedAmountFields,
  criticalIllnessTypedAmountFields,
  dentalHmoTypedAmountFields,
  dentalPpoTypedAmountFields,
  fmlTypedAmountFields,
  hospitalTypedAmountFields,
  idiTypedAmountFields,
  ltdTypedAmountFields,
  stdTypedAmountFields,
  visionTypedAmountFields,
  volLifeTypedAmountFields,
  wholeLifeTypedAmountFields,
  COLLAPSED_SIBLINGS_KEY,
} from "@platform/formatting-utils";
import { type ChildAgeBand } from "@platform/plan-grid-core";

import { normalizeFieldKeyPart } from "../utils/popupCustomText";

import {
  AgeReductionScheduleCellEditor,
  type AgeReductionScheduleEntry,
  type AgeReductionTypeOption,
} from "./AgeReductionScheduleCellEditor";
import {
  BenefitDescriptionCellEditor,
  type BenefitDescriptionTypeOption,
} from "./BenefitDescriptionCellEditor";
import { ChildBenefitAgeBandsCellEditor } from "./ChildBenefitAgeBandsCellEditor";
import { type BenefitDescriptionEntryLimits } from "./benefitDescriptionValues";
import {
  GuaranteeIssueCellEditor,
  type GuaranteeIssueScalarInput,
} from "./GuaranteeIssueCellEditor";
import { type AgeBasedAmountEntry } from "./AgeBandAmountsField";
import { FieldPopoverEditor } from "./FieldPopoverEditor";
import { GroupedEnumCellEditor } from "./GroupedEnumCellEditor";
import { PreExistingConditionCellEditor } from "./PreExistingConditionCellEditor";
import { ServiceWaitingPeriodsCellEditor } from "./ServiceWaitingPeriodsCellEditor";
import {
  TypedAmountCellEditor,
  type TypedAmountExtraInput,
} from "./TypedAmountCellEditor";

import type { JsonValue } from "../utils/popupCustomText";
import type {
  FieldPopoverEditorOption,
  FieldValueType,
} from "./FieldPopoverEditor";
import type { TypedAmountSpec } from "@platform/formatting-utils";
import type { ServiceWaitingPeriod } from "@platform/plan-grid-core";
import type { PreExistingConditionTypeOption } from "./PreExistingConditionCellEditor";
import type { ICellEditorParams } from "ag-grid-community";
import type { ComponentType } from "react";

export type QuotesPopupCustomTextByCellKey = ReadonlyMap<string, string>;

export interface QuotesPopupCustomTextOptions {
  customTextByCellKey: QuotesPopupCustomTextByCellKey;
  onCustomTextChange: (
    cellKey: string,
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

interface QuotesPopupCustomTextCellKeyParams {
  colDef?: {
    field?: string;
  };
  data?: unknown;
}

const TYPED_AMOUNT_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, Record<string, TypedAmountSpec>>
> = {
  [CoverageTypeEnum.ACCIDENT]: accidentTypedAmountFields,
  [CoverageTypeEnum.BASIC_LIFE_ADND]: basicLifeTypedAmountFields,
  [CoverageTypeEnum.CANCER]: cancerTypedAmountFields,
  [CoverageTypeEnum.CRITICAL_ILLNESS]: criticalIllnessTypedAmountFields,
  [CoverageTypeEnum.DENTAL_HMO]: dentalHmoTypedAmountFields,
  [CoverageTypeEnum.DENTAL_PPO]: dentalPpoTypedAmountFields,
  [CoverageTypeEnum.FMLA]: fmlTypedAmountFields,
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: hospitalTypedAmountFields,
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: idiTypedAmountFields,
  [CoverageTypeEnum.LTD]: ltdTypedAmountFields,
  [CoverageTypeEnum.STD]: stdTypedAmountFields,
  [CoverageTypeEnum.VISION]: visionTypedAmountFields,
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: volLifeTypedAmountFields,
  [CoverageTypeEnum.WHOLE_LIFE]: wholeLifeTypedAmountFields,
};

const NOT_INCLUDED_OPTION = { value: "notIncluded", label: "Not Included" };
const MONTHS_OPTION = { value: "months", label: "Months" };
const WAIVED_OPTION = { value: "waived", label: "Waived" };
const NONE_OPTION = { value: "none", label: "None" };
const NOT_OFFERED_OPTION = { value: "notOffered", label: "Not Offered" };
const NOT_DISCLOSED_OPTION = { value: "notDisclosed", label: "Not Disclosed" };
const DOLLAR_AMOUNT_LABEL = "Dollar Amount";

// Plain enum fields (no dedicated editor) whose per-coverage PPX requirements
// add `notOffered` — a frontend-only sentinel absent from these v5 enums.
// Coverage-scoped so Build (which never runs this override) is unaffected.
const NOT_OFFERED_ALLOWED_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, Set<string>>
> = {
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: new Set<string>([
    "adndIncluded",
    "adndTiedToLifeElection",
    "conversion",
    "portability",
    "acceleratedDeathBenefit",
    "waiverOfPremium",
    "annualOpenEnrollment",
  ]),
};

// Not Disclosed is offered on every Quotes dropdown (CD-2547). Appended here,
// rather than added to the shared benefit definitions and specs, because those
// also drive Build, where several fields deliberately omit it.
const withNotDisclosed = (
  options: FieldPopoverEditorOption[],
): FieldPopoverEditorOption[] =>
  options.some((option) => option.value === NOT_DISCLOSED_OPTION.value)
    ? options
    : [...options, NOT_DISCLOSED_OPTION];

// A Not Disclosed type carries no amount, so the typed-amount editor hides
// its amount input for it.
const withNotDisclosedHidingAmount = (
  options: TypedAmountSpec["typeOptions"],
): TypedAmountSpec["typeOptions"] =>
  options.some((option) => option.value === NOT_DISCLOSED_OPTION.value)
    ? options
    : [...options, { ...NOT_DISCLOSED_OPTION, hideAmount: true }];

const withNotOffered = (
  options: FieldPopoverEditorOption[],
): FieldPopoverEditorOption[] =>
  options.some((option) => option.value === NOT_OFFERED_OPTION.value)
    ? options
    : [...options, NOT_OFFERED_OPTION];

interface PreExistingConditionSpec {
  typeKey: string;
  detailKey: string;
  typeOptions: PreExistingConditionTypeOption[];
  /** Upper bound for the month sub-fields, when the backend enforces one
   * (e.g. STD's ent schema caps these at 60) — undefined when unbounded. */
  monthsMax?: number;
}

const STD_PRE_EXISTING_CONDITION_MONTHS_MAX = 60;

const PRE_EXISTING_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, PreExistingConditionSpec>
> = {
  [CoverageTypeEnum.CANCER]: {
    typeKey: "preExistingConditionType",
    detailKey: "preExistingConditionMonthsDetail",
    typeOptions: [MONTHS_OPTION, NOT_OFFERED_OPTION],
  },
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: {
    typeKey: "preExistingConditionType",
    detailKey: "preExistingConditionMonthsDetail",
    typeOptions: [
      MONTHS_OPTION,
      WAIVED_OPTION,
      NOT_OFFERED_OPTION,
      NONE_OPTION,
    ],
  },
  [CoverageTypeEnum.CRITICAL_ILLNESS]: {
    typeKey: "preExistingConditionType",
    detailKey: "preExistingConditionMonthsDetail",
    typeOptions: [MONTHS_OPTION, WAIVED_OPTION, NONE_OPTION],
  },
  [CoverageTypeEnum.STD]: {
    typeKey: "preExistingConditionType",
    detailKey: "preExistingConditionMonthsDetail",
    typeOptions: [
      MONTHS_OPTION,
      WAIVED_OPTION,
      NOT_OFFERED_OPTION,
      NONE_OPTION,
    ],
    monthsMax: STD_PRE_EXISTING_CONDITION_MONTHS_MAX,
  },
  [CoverageTypeEnum.LTD]: {
    typeKey: "preExistingConditionType",
    detailKey: "preExistingConditionMonthsDetail",
    typeOptions: [
      MONTHS_OPTION,
      WAIVED_OPTION,
      NOT_OFFERED_OPTION,
      NONE_OPTION,
    ],
  },
};

interface AgeReductionScheduleSpec {
  typeKey: string;
  detailKey: string;
  typeOptions: AgeReductionTypeOption[];
  /** The type value that requires a schedule. */
  schedulesForType: string;
}

// Every LOC builds age reduction from the same shared builder (typeKey
// `ageReductionScheduleType`, schedule sibling `ageReductionScheduleSchedules`,
// type `percentageAtAge`), so a single spec shape covers them all.
const AGE_REDUCTION_SCHEDULE_SPEC: AgeReductionScheduleSpec = {
  typeKey: "ageReductionScheduleType",
  detailKey: "ageReductionScheduleSchedules",
  schedulesForType: "percentageAtAge",
  typeOptions: [
    { value: "percentageAtAge", label: "Percentage at Age" },
    NOT_INCLUDED_OPTION,
  ],
};

// Voluntary Life's, Accident's and Critical Illness's PPX requirements add
// "Not Offered" to the age-reduction dropdown; the shared spec above stays
// as-is for the other LOCs.
const AGE_REDUCTION_SCHEDULE_WITH_NOT_OFFERED_SPEC: AgeReductionScheduleSpec = {
  ...AGE_REDUCTION_SCHEDULE_SPEC,
  typeOptions: [
    { value: "percentageAtAge", label: "Percentage at Age" },
    NOT_INCLUDED_OPTION,
    NOT_OFFERED_OPTION,
  ],
};

// Most LOCs have exactly one age-reduction field, so a single spec covers
// them. Voluntary Life has two independent ones (Employee and Spouse), each
// with its own type/schedule field pair, so its entry is an array instead.
const AGE_REDUCTION_BY_COVERAGE: Partial<
  Record<
    CoverageTypeEnum,
    AgeReductionScheduleSpec | AgeReductionScheduleSpec[]
  >
> = {
  [CoverageTypeEnum.ACCIDENT]: AGE_REDUCTION_SCHEDULE_WITH_NOT_OFFERED_SPEC,
  [CoverageTypeEnum.CRITICAL_ILLNESS]:
    AGE_REDUCTION_SCHEDULE_WITH_NOT_OFFERED_SPEC,
  [CoverageTypeEnum.BASIC_LIFE_ADND]: AGE_REDUCTION_SCHEDULE_SPEC,
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: [
    // The existing v5 ageReductionScheduleType/Schedules pair, unchanged —
    // still persisted by transformVoluntaryLifeBenefitsToApi.
    AGE_REDUCTION_SCHEDULE_WITH_NOT_OFFERED_SPEC,
    {
      ...AGE_REDUCTION_SCHEDULE_WITH_NOT_OFFERED_SPEC,
      typeKey: "ageReductionsEe",
      detailKey: "employeeAgeReductionSchedules",
    },
    {
      ...AGE_REDUCTION_SCHEDULE_WITH_NOT_OFFERED_SPEC,
      typeKey: "ageReductionsSp",
      detailKey: "spouseAgeReductionSchedules",
    },
  ],
};

const getAgeReductionSpecs = (
  selectedCoverageType: CoverageTypeEnum,
): AgeReductionScheduleSpec[] => {
  const entry = AGE_REDUCTION_BY_COVERAGE[selectedCoverageType];
  if (!entry) return [];
  return Array.isArray(entry) ? entry : [entry];
};

// Voluntary Life Guarantee Issue: a type whose inputs vary — a salary
// multiplier + dollar/cap (+ additional amount), or an `ageBasedAmounts` list
// of `$X for ages Y–Z` bands. Age-based amounts are a VL-only shape, so this
// routes to the dedicated GuaranteeIssueCellEditor rather than a typed amount.
const GUARANTEE_ISSUE_AGE_BASED = "ageBasedAmounts";
const GUARANTEE_ISSUE_WITH_CAP = "salaryMultiplierWithCap";
const GUARANTEE_ISSUE_WITH_ADDITIONAL =
  "salaryMultiplierWithDollarAmountPlusAdditionalAmount";

const GUARANTEE_ISSUE_TYPE_OPTIONS = [
  { value: "dollarAmount", label: DOLLAR_AMOUNT_LABEL },
  { value: GUARANTEE_ISSUE_AGE_BASED, label: "Age-Based Amounts" },
  { value: GUARANTEE_ISSUE_WITH_CAP, label: "Salary Multiplier With Cap" },
  {
    value: GUARANTEE_ISSUE_WITH_ADDITIONAL,
    label: "Salary Multiplier With Dollar Amount Plus Additional Amount",
  },
  { value: "allGuaranteeIssue", label: "All Guarantee Issue" },
  NOT_INCLUDED_OPTION,
  NOT_OFFERED_OPTION,
];

interface GuaranteeIssueSpec {
  typeKey: string;
  typeOptions: { value: string; label: string }[];
  agesDetailsKey: string;
  bandsForType: string;
  scalarInputs: Omit<GuaranteeIssueScalarInput, "initialValue">[];
}

// Gating mirrors the BTX definitions: the multiplier shows for the two salary
// multiplier types; the dollar/cap for those plus a plain dollar amount; the
// additional amount only for the "plus additional" type.
const buildGuaranteeIssueSpec = (prefix: string): GuaranteeIssueSpec => ({
  typeKey: `${prefix}Type`,
  typeOptions: GUARANTEE_ISSUE_TYPE_OPTIONS,
  agesDetailsKey: `${prefix}AgesDetails`,
  bandsForType: GUARANTEE_ISSUE_AGE_BASED,
  scalarInputs: [
    {
      key: `${prefix}SalaryMultiplier`,
      label: "Salary Multiplier",
      showForTypes: [GUARANTEE_ISSUE_WITH_CAP, GUARANTEE_ISSUE_WITH_ADDITIONAL],
    },
    {
      key: `${prefix}DollarAmount`,
      label: DOLLAR_AMOUNT_LABEL,
      showForTypes: [
        "dollarAmount",
        GUARANTEE_ISSUE_WITH_CAP,
        GUARANTEE_ISSUE_WITH_ADDITIONAL,
      ],
    },
    {
      key: `${prefix}AdditionalAmount`,
      label: "Additional Amount",
      showForTypes: [GUARANTEE_ISSUE_WITH_ADDITIONAL],
    },
  ],
});

const GUARANTEE_ISSUE_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, Record<string, GuaranteeIssueSpec>>
> = {
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: {
    guaranteeIssueEmployeeType: buildGuaranteeIssueSpec(
      "guaranteeIssueEmployee",
    ),
    guaranteeIssueSpouseType: buildGuaranteeIssueSpec("guaranteeIssueSpouse"),
  },
};

export interface BenefitDescriptionSpec {
  typeKey: string;
  amountKey: string;
  /** Omitted for coverages whose benefit description has no Minimum sibling. */
  minimumKey?: string;
  typeOptions: BenefitDescriptionTypeOption[];
  entryLimits?: BenefitDescriptionEntryLimits;
}

const BENEFIT_DESCRIPTION_SHARED_OPTIONS: BenefitDescriptionTypeOption[] = [
  { value: "increments", label: "Increments" },
  { value: "percentage", label: "Percentage" },
  { value: "dollarAmount", label: "Dollar Amount" },
  { value: "multipleDollarAmounts", label: "Multiple Dollar Amounts" },
];

const MULTIPLE_DOLLAR_AMOUNTS_OPTION: BenefitDescriptionTypeOption = {
  value: "multipleDollarAmounts",
  label: "Multiple Dollar Amounts",
};
const MULTIPLE_SALARY_MULTIPLIERS_OPTION: BenefitDescriptionTypeOption = {
  value: "multipleSalaryMultipliers",
  label: "Multiple Salary Multipliers",
};
const SALARY_MULTIPLIER_OPTION: BenefitDescriptionTypeOption = {
  value: "salaryMultiplier",
  label: "Salary Multiplier",
};

// Life benefit descriptions accept a list of dollar amounts or a list of
// salary multipliers; Build allows 2–10 entries for both, and the two frontend
// types collapse back onto the API's single `multipleOptions`/`salaryMultiplier`
// enum plus an `amounts` array in the shared transform.
const LIFE_BENEFIT_ENTRY_LIMITS: BenefitDescriptionEntryLimits = {
  min: 2,
  max: 10,
};

const LIFE_EMPLOYEE_BENEFIT_OPTIONS: BenefitDescriptionTypeOption[] = [
  { value: "increments", label: "Increments" },
  { value: "dollarAmount", label: DOLLAR_AMOUNT_LABEL },
  SALARY_MULTIPLIER_OPTION,
  MULTIPLE_DOLLAR_AMOUNTS_OPTION,
  MULTIPLE_SALARY_MULTIPLIERS_OPTION,
  NOT_INCLUDED_OPTION,
];

// Vol Life's employee benefit description omits Not Included per the PropelX
// requirements sheet — its employee type enum has no notIncluded value.
const VOLUNTARY_LIFE_EMPLOYEE_BENEFIT_OPTIONS: BenefitDescriptionTypeOption[] =
  [
    { value: "increments", label: "Increments" },
    { value: "dollarAmount", label: DOLLAR_AMOUNT_LABEL },
    SALARY_MULTIPLIER_OPTION,
    MULTIPLE_DOLLAR_AMOUNTS_OPTION,
    MULTIPLE_SALARY_MULTIPLIERS_OPTION,
  ];

// Spouse and child benefit descriptions have no salary-multiplier type in the
// API enum (unlike the employee's), so neither multiplier option appears here.
const VOLUNTARY_LIFE_DEPENDENT_BENEFIT_OPTIONS: BenefitDescriptionTypeOption[] =
  [
    { value: "increments", label: "Increments" },
    { value: "dollarAmount", label: DOLLAR_AMOUNT_LABEL },
    {
      value: "percentageOfEmployeeElection",
      label: "Percentage of EE Election",
    },
    MULTIPLE_DOLLAR_AMOUNTS_OPTION,
    NOT_INCLUDED_OPTION,
  ];

const buildLifeBenefitDescriptionSpec = (
  prefix: string,
  typeOptions: BenefitDescriptionTypeOption[],
): BenefitDescriptionSpec => ({
  typeKey: `${prefix}Type`,
  amountKey: `${prefix}Amount`,
  typeOptions,
  entryLimits: LIFE_BENEFIT_ENTRY_LIMITS,
});

const BENEFIT_DESCRIPTION_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, Record<string, BenefitDescriptionSpec>>
> = {
  [CoverageTypeEnum.CRITICAL_ILLNESS]: {
    employeeBenefitDescriptionType: {
      typeKey: "employeeBenefitDescriptionType",
      amountKey: "employeeBenefitDescriptionAmount",
      minimumKey: "employeeBenefitDescriptionMinimum",
      typeOptions: [
        ...BENEFIT_DESCRIPTION_SHARED_OPTIONS,
        NOT_DISCLOSED_OPTION,
      ],
    },
    spouseBenefitDescriptionType: {
      typeKey: "spouseBenefitDescriptionType",
      amountKey: "spouseBenefitDescriptionAmount",
      minimumKey: "spouseBenefitDescriptionMinimum",
      typeOptions: [...BENEFIT_DESCRIPTION_SHARED_OPTIONS, NOT_INCLUDED_OPTION],
    },
    childBenefitDescriptionType: {
      typeKey: "childBenefitDescriptionType",
      amountKey: "childBenefitDescriptionAmount",
      minimumKey: "childBenefitDescriptionMinimum",
      typeOptions: [
        { value: "percentageOfEeBenefit", label: "Percentage of EE Benefit" },
        { value: "flatAmount", label: "Flat Amount" },
        NOT_INCLUDED_OPTION,
      ],
    },
  },
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: {
    benefitDescriptionEmployeeType: buildLifeBenefitDescriptionSpec(
      "benefitDescriptionEmployee",
      VOLUNTARY_LIFE_EMPLOYEE_BENEFIT_OPTIONS,
    ),
    benefitDescriptionSpouseType: buildLifeBenefitDescriptionSpec(
      "benefitDescriptionSpouse",
      VOLUNTARY_LIFE_DEPENDENT_BENEFIT_OPTIONS,
    ),
    benefitDescriptionChildDependentAgesBirthTo14DaysType:
      buildLifeBenefitDescriptionSpec(
        "benefitDescriptionChildDependentAgesBirthTo14Days",
        VOLUNTARY_LIFE_DEPENDENT_BENEFIT_OPTIONS,
      ),
    benefitDescriptionChildDependentAges15DaysTo6MonthsType:
      buildLifeBenefitDescriptionSpec(
        "benefitDescriptionChildDependentAges15DaysTo6Months",
        VOLUNTARY_LIFE_DEPENDENT_BENEFIT_OPTIONS,
      ),
    benefitDescriptionChildDependentAges6MonthsToLimitingAgeType:
      buildLifeBenefitDescriptionSpec(
        "benefitDescriptionChildDependentAges6MonthsToLimitingAge",
        VOLUNTARY_LIFE_DEPENDENT_BENEFIT_OPTIONS,
      ),
  },
  [CoverageTypeEnum.WHOLE_LIFE]: {
    employeeBenefitAmountType: buildLifeBenefitDescriptionSpec(
      "employeeBenefitAmount",
      LIFE_EMPLOYEE_BENEFIT_OPTIONS,
    ),
  },
};

interface GroupedEnumFieldSpec {
  key: string;
  label: string;
}

interface GroupedEnumSpec {
  /** The benefitKey of the surviving row after sibling collapse — must
   * match `fields[0].key`. */
  parentKey: string;
  fields: GroupedEnumFieldSpec[];
}

// Vision's Frequency - Exam/Lenses/Frames: three independent, ungated enum
// fields collapsed into the examFrequency row by collapseAmountRows. All
// three share the same enum (VisionBenefitFrequency, extended with
// notDisclosed/notOffered) via examFrequency's own cell spec, so the popup's
// options are resolved from that spec rather than duplicated here.
const GROUPED_ENUM_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, GroupedEnumSpec>
> = {
  [CoverageTypeEnum.VISION]: {
    parentKey: "examFrequency",
    fields: [
      { key: "examFrequency", label: "Exam" },
      { key: "lensesFrequency", label: "Lenses" },
      { key: "framesFrequency", label: "Frames" },
    ],
  },
};

// Dental HMO and PPO's Service Waiting Periods row — the only benefitKey
// that shares one `sharedCellSpecs.serviceWaitingPeriods` cell spec across
// both LOCs, so no per-coverage registry is needed.
const SERVICE_WAITING_PERIODS_BENEFIT_KEY = "serviceWaitingPeriods";

// Vol Life's flexible child benefit-description row (CD-1976): the single
// Present row that replaces Build's three fixed age-band rows. Its value is the
// full `benefitAmountsByAge` array, edited by ChildBenefitAgeBandsCellEditor.
const CHILD_AGE_BANDS_BENEFIT_KEY = "childBenefitAmountsByAge";

const buildTypedAmountSpecByBenefitKey = (
  registry: Record<string, TypedAmountSpec>,
): Record<string, TypedAmountSpec> => {
  const byBenefitKey: Record<string, TypedAmountSpec> = {};

  for (const [benefitKey, spec] of Object.entries(registry)) {
    byBenefitKey[benefitKey] = spec;
    byBenefitKey[spec.typeKey] = spec;
  }

  return byBenefitKey;
};

/**
 * Builds the cell-editor override for the Quotes tab.
 *
 * Resolution order:
 *
 * 1. Typed-amount popup (`TypedAmountCellEditor`) for cells whose row
 *    benefitKey matches a registered `{type, amount}` spec for the
 *    selected coverage. Cancer is the only coverage with a registry
 *    today; others fall through.
 * 2. Pre-existing condition popup (`PreExistingConditionCellEditor`) for the
 *    `preExistingConditionType` row — type dropdown plus up to three month
 *    number inputs that write back to the collapsed `preExistingConditionMonthsDetail`
 *    sibling string.
 * 3. Grouped-enum popup (`GroupedEnumCellEditor`) for a row that is the
 *    surviving parent of a `GROUPED_ENUM_BY_COVERAGE` entry — e.g. Vision's
 *    `examFrequency`, which collapses `lensesFrequency`/`framesFrequency`
 *    into itself. Renders one always-visible dropdown per field, all
 *    sharing the parent row's own enum options.
 * 4. Service Waiting Periods popup (`ServiceWaitingPeriodsCellEditor`) for
 *    Dental's `serviceWaitingPeriods` row — replaces the shared
 *    `plan-grid-core` editor (which has no custom-text concept) with a
 *    Quotes-side wrapper offering the same Edit Benefit/Enter Custom Text
 *    toggle as every other field.
 * 5. Generic single-field popover (`FieldPopoverEditor`) for any other
 *    editable scalar cell — enum, number, or text — inferred from the
 *    cellSpec on `data.benefit[.properties[propertyKey]].cell`.
 * 6. Fall through (`undefined`) for cellSpecs that already specify a
 *    custom non-`ag*` editor (e.g. `PopoverListEditor` for arrays) so
 *    we don't shadow specialized editors.
 *
 * Editability itself (which columns may even reach the editor) is
 * already gated by `editableNonCurrentCarriers: true` in btx —
 * proposal columns only; the requested/incumbent column is anchored
 * read-only.
 */
export const buildQuotesCellEditorOverride = (
  selectedCoverageType: CoverageTypeEnum | undefined,
  customTextOptions?: QuotesPopupCustomTextOptions,
): CellEditorOverride | null => {
  if (!selectedCoverageType) return null;
  const typedAmountRegistry = TYPED_AMOUNT_BY_COVERAGE[selectedCoverageType];
  const typedAmountByBenefitKey = typedAmountRegistry
    ? buildTypedAmountSpecByBenefitKey(typedAmountRegistry)
    : undefined;
  const preExistingSpec = PRE_EXISTING_BY_COVERAGE[selectedCoverageType];
  const ageReductionSpecs = getAgeReductionSpecs(selectedCoverageType);
  const guaranteeIssueSpecs = GUARANTEE_ISSUE_BY_COVERAGE[selectedCoverageType];
  const benefitDescriptionSpecs =
    BENEFIT_DESCRIPTION_BY_COVERAGE[selectedCoverageType];
  const groupedEnumSpec = GROUPED_ENUM_BY_COVERAGE[selectedCoverageType];
  const notOfferedAllowed =
    NOT_OFFERED_ALLOWED_BY_COVERAGE[selectedCoverageType] ?? new Set<string>();

  return (params) => {
    const benefitKey = (params.data as { benefitKey?: string } | undefined)
      ?.benefitKey;

    if (typedAmountByBenefitKey) {
      const spec = benefitKey ? typedAmountByBenefitKey[benefitKey] : undefined;
      if (spec) return buildTypedAmountResult(params, spec, customTextOptions);
    }

    if (preExistingSpec && benefitKey === preExistingSpec.typeKey) {
      return buildPreExistingResult(params, preExistingSpec, customTextOptions);
    }

    const matchedAgeReductionSpec = ageReductionSpecs.find(
      (spec) => benefitKey === spec.typeKey,
    );
    if (matchedAgeReductionSpec) {
      return buildAgeReductionResult(
        params,
        matchedAgeReductionSpec,
        customTextOptions,
      );
    }

    const guaranteeIssueSpec =
      guaranteeIssueSpecs && benefitKey
        ? guaranteeIssueSpecs[benefitKey]
        : undefined;
    if (guaranteeIssueSpec) {
      return buildGuaranteeIssueResult(
        params,
        guaranteeIssueSpec,
        customTextOptions,
      );
    }

    const benefitDescriptionSpec =
      benefitDescriptionSpecs && benefitKey
        ? benefitDescriptionSpecs[benefitKey]
        : undefined;
    if (benefitDescriptionSpec) {
      return buildBenefitDescriptionResult(
        params,
        benefitDescriptionSpec,
        customTextOptions,
      );
    }

    if (groupedEnumSpec && benefitKey === groupedEnumSpec.parentKey) {
      return buildGroupedEnumResult(params, groupedEnumSpec, customTextOptions);
    }

    if (benefitKey === SERVICE_WAITING_PERIODS_BENEFIT_KEY) {
      return buildServiceWaitingPeriodsResult(params, customTextOptions);
    }

    if (benefitKey === CHILD_AGE_BANDS_BENEFIT_KEY) {
      return buildChildAgeBandsResult(params, customTextOptions);
    }

    return buildGenericFieldResult(
      params,
      customTextOptions,
      notOfferedAllowed,
    );
  };
};

export const buildQuotesPopupCustomTextCellKey = (
  params: QuotesPopupCustomTextCellKeyParams,
): string | undefined => {
  const columnField = params.colDef?.field;
  const data = params.data as
    | { benefitKey?: string; propertyKey?: string }
    | undefined;
  if (!columnField || !data?.benefitKey) return undefined;

  return [columnField, data.benefitKey, data.propertyKey]
    .map(normalizeFieldKeyPart)
    .join("::");
};

const buildCustomTextEditorParams = (
  params: ICellEditorParams,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
):
  | {
      initialCustomText: string | null;
      onCustomTextChange: (
        customText: string | null,
        originalValue?: JsonValue,
      ) => void;
    }
  | undefined => {
  if (!customTextOptions) return undefined;

  const cellKey = buildQuotesPopupCustomTextCellKey(params);
  if (!cellKey) return undefined;

  return {
    initialCustomText:
      customTextOptions.customTextByCellKey.get(cellKey) ?? null,
    onCustomTextChange: (customText, originalValue) => {
      customTextOptions.onCustomTextChange(cellKey, customText, originalValue);
    },
  };
};

const buildTypedAmountResult = (
  params: ICellEditorParams,
  spec: TypedAmountSpec,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
): CellEditorOverrideResult => {
  const initialType = getCellValueInCurrentColumn(params, spec.typeKey);

  const collapsedSiblings = (
    params.data as
      | {
          [COLLAPSED_SIBLINGS_KEY]?: Record<string, Record<string, unknown>>;
        }
      | undefined
  )?.[COLLAPSED_SIBLINGS_KEY];
  const columnField = params.colDef?.field;

  const readCollapsedSibling = (key: string): unknown => {
    if (!columnField) return undefined;
    const byColumn = collapsedSiblings?.[key];
    if (!byColumn) return undefined;
    if (columnField in byColumn) return byColumn[columnField];
    return byColumn[columnField.split(".")[0]];
  };

  const collapsedAmount = readCollapsedSibling(spec.amountKey);
  const rawAmount =
    collapsedAmount !== undefined
      ? collapsedAmount
      : getCellValueInCurrentColumn(params, spec.amountKey);
  const initialAmount =
    typeof rawAmount === "number"
      ? rawAmount
      : rawAmount === null
        ? null
        : undefined;

  const { amountMin, amountInteger, amountOptional, extraKeys, secondary } =
    spec;
  const extraInputs: TypedAmountExtraInput[] | undefined = extraKeys?.map(
    (e) => {
      const collapsed = readCollapsedSibling(e.key);
      const raw =
        collapsed !== undefined
          ? collapsed
          : getCellValueInCurrentColumn(params, e.key);
      return {
        key: e.key,
        label: e.label,
        initialValue:
          typeof raw === "number" ? raw : raw === null ? null : undefined,
        showForTypes: e.showForTypes,
        defaultValue: e.defaultValue,
        min: e.min,
        max: e.max,
        step: e.step,
      };
    },
  );

  const { textExtra: textExtraSpec } = spec;
  const textExtra = textExtraSpec
    ? {
        key: textExtraSpec.key,
        label: textExtraSpec.label,
        maxLength: textExtraSpec.maxLength,
        initialValue: (() => {
          const raw =
            readCollapsedSibling(textExtraSpec.key) ??
            getCellValueInCurrentColumn(params, textExtraSpec.key);
          return typeof raw === "string" ? raw : (raw as null | undefined);
        })(),
      }
    : undefined;

  const secondaryGroup = secondary
    ? {
        label: secondary.label,
        typeKey: secondary.typeKey,
        amountKey: secondary.amountKey,
        amountLabel: secondary.amountLabel,
        typeOptions: withNotDisclosedHidingAmount(secondary.typeOptions),
        hideForTypes: secondary.hideForTypes,
        initialTypedAmountValue: {
          type: (() => {
            const raw = readCollapsedSibling(secondary.typeKey);
            return typeof raw === "string" ? raw : undefined;
          })(),
          amount: (() => {
            const raw = readCollapsedSibling(secondary.amountKey);
            return typeof raw === "number"
              ? raw
              : raw === null
                ? null
                : undefined;
          })(),
        },
      }
    : undefined;

  return {
    component: TypedAmountCellEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      initialTypedAmountValue: {
        type: typeof initialType === "string" ? initialType : undefined,
        amount:
          typeof initialAmount === "number"
            ? initialAmount
            : initialAmount === null
              ? null
              : undefined,
      },
      typeOptions: withNotDisclosedHidingAmount(spec.typeOptions),
      amountLabel: spec.amountLabel,
      typeKey: spec.typeKey,
      amountKey: spec.amountKey,
      amountMin,
      amountInteger,
      amountOptional,
      ...(extraInputs?.length ? { extraInputs } : {}),
      ...(secondaryGroup ? { secondaryGroup } : {}),
      ...(textExtra ? { textExtra } : {}),
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};

const buildPreExistingResult = (
  params: ICellEditorParams,
  spec: PreExistingConditionSpec,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
): CellEditorOverrideResult => {
  const columnField = params.colDef?.field;
  // The preExistingConditionType row IS the current row being edited, so its
  // enum value lives directly in params.data[columnField], not in a sibling.
  const rowData = params.data as Record<string, unknown> | undefined;
  const rawType = columnField && rowData ? rowData[columnField] : undefined;
  const initialType = typeof rawType === "string" ? rawType : undefined;
  const collapsedSiblings = (
    params.data as
      | { [COLLAPSED_SIBLINGS_KEY]?: Record<string, Record<string, unknown>> }
      | undefined
  )?.[COLLAPSED_SIBLINGS_KEY];
  const rawDetail =
    columnField && collapsedSiblings?.[spec.detailKey]?.[columnField];
  return {
    component:
      PreExistingConditionCellEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      initialType: typeof initialType === "string" ? initialType : undefined,
      initialMonthsDetail:
        typeof rawDetail === "string" ? rawDetail : undefined,
      typeKey: spec.typeKey,
      detailKey: spec.detailKey,
      typeOptions: withNotDisclosed(spec.typeOptions),
      monthsMax: spec.monthsMax,
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};

// A popover-editor cell value arrives as an array or, from some persisted
// shapes, a JSON-encoded string. Coerce either to an array (empty otherwise).
const parseMaybeJsonArray = (raw: unknown): unknown[] => {
  let list: unknown = raw;

  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  return Array.isArray(list) ? list : [];
};

// Coerce the schedule sibling into typed `{reductionPercent, age}` bands.
const parseSchedules = (raw: unknown): AgeReductionScheduleEntry[] =>
  parseMaybeJsonArray(raw)
    .map((entry) => {
      const { reductionPercent, age } = (entry ?? {}) as {
        reductionPercent?: unknown;
        age?: unknown;
      };

      return {
        reductionPercent: Number(reductionPercent),
        age: Number(age),
      };
    })
    .filter(
      (entry) =>
        Number.isFinite(entry.reductionPercent) && Number.isFinite(entry.age),
    );

const buildAgeReductionResult = (
  params: ICellEditorParams,
  spec: AgeReductionScheduleSpec,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
): CellEditorOverrideResult => {
  const columnField = params.colDef?.field;

  // The type row IS the current row, so its enum value lives directly in
  // params.data[columnField], not in a sibling.
  const rowData = params.data as Record<string, unknown> | undefined;
  const rawType = columnField && rowData ? rowData[columnField] : undefined;

  const collapsedSiblings = (
    params.data as
      | { [COLLAPSED_SIBLINGS_KEY]?: Record<string, Record<string, unknown>> }
      | undefined
  )?.[COLLAPSED_SIBLINGS_KEY];

  const collapsed = columnField
    ? collapsedSiblings?.[spec.detailKey]?.[columnField]
    : undefined;

  const rawSchedules =
    collapsed !== undefined
      ? collapsed
      : getCellValueInCurrentColumn(params, spec.detailKey);

  return {
    component:
      AgeReductionScheduleCellEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      initialType: typeof rawType === "string" ? rawType : undefined,
      initialSchedules: parseSchedules(rawSchedules),
      typeKey: spec.typeKey,
      detailKey: spec.detailKey,
      typeOptions: withNotDisclosed(spec.typeOptions),
      schedulesForType: spec.schedulesForType,
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};

// Coerce age-based amounts into typed `{amount, ageLower, ageUpper}` bands.
const parseAgeBands = (raw: unknown): AgeBasedAmountEntry[] =>
  parseMaybeJsonArray(raw)
    .map((entry) => {
      const { amount, ageLower, ageUpper } = (entry ?? {}) as {
        amount?: unknown;
        ageLower?: unknown;
        ageUpper?: unknown;
      };

      return {
        amount: Number(amount),
        ageLower: Number(ageLower),
        ageUpper: Number(ageUpper),
      };
    })
    .filter(
      (band) =>
        Number.isFinite(band.amount) &&
        Number.isFinite(band.ageLower) &&
        Number.isFinite(band.ageUpper),
    );

const buildGuaranteeIssueResult = (
  params: ICellEditorParams,
  spec: GuaranteeIssueSpec,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
): CellEditorOverrideResult => {
  const columnField = params.colDef?.field;

  // The type row IS the current row, so its enum value lives directly in
  // params.data[columnField]; the amount siblings live in __collapsedSiblings.
  const rowData = params.data as Record<string, unknown> | undefined;
  const rawType = columnField && rowData ? rowData[columnField] : undefined;
  const collapsedSiblings = (
    params.data as
      | { [COLLAPSED_SIBLINGS_KEY]?: Record<string, Record<string, unknown>> }
      | undefined
  )?.[COLLAPSED_SIBLINGS_KEY];

  const readSibling = (key: string): unknown =>
    columnField ? collapsedSiblings?.[key]?.[columnField] : undefined;

  const scalarInputs: GuaranteeIssueScalarInput[] = spec.scalarInputs.map(
    (input) => {
      const raw = readSibling(input.key);
      return {
        ...input,
        initialValue:
          typeof raw === "number" ? raw : raw === null ? null : undefined,
      };
    },
  );

  return {
    component: GuaranteeIssueCellEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      initialType: typeof rawType === "string" ? rawType : undefined,
      typeKey: spec.typeKey,
      typeOptions: withNotDisclosed(spec.typeOptions),
      scalarInputs,
      agesDetailsKey: spec.agesDetailsKey,
      bandsForType: spec.bandsForType,
      initialBands: parseAgeBands(readSibling(spec.agesDetailsKey)),
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};

const buildBenefitDescriptionResult = (
  params: ICellEditorParams,
  spec: BenefitDescriptionSpec,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
): CellEditorOverrideResult => {
  const columnField = params.colDef?.field;

  // The type row IS the current row, so its enum value lives directly in
  // params.data[columnField], not in a sibling.
  const rowData = params.data as Record<string, unknown> | undefined;
  const rawType = columnField && rowData ? rowData[columnField] : undefined;

  const collapsedSiblings = (
    params.data as
      | { [COLLAPSED_SIBLINGS_KEY]?: Record<string, Record<string, unknown>> }
      | undefined
  )?.[COLLAPSED_SIBLINGS_KEY];

  const readSibling = (key: string): unknown => {
    const collapsed = columnField
      ? collapsedSiblings?.[key]?.[columnField]
      : undefined;

    return collapsed !== undefined
      ? collapsed
      : getCellValueInCurrentColumn(params, key);
  };

  const rawMinimum = spec.minimumKey ? readSibling(spec.minimumKey) : undefined;

  return {
    component:
      BenefitDescriptionCellEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      initialType: typeof rawType === "string" ? rawType : undefined,
      initialAmount: readSibling(spec.amountKey),
      initialMinimum: typeof rawMinimum === "number" ? rawMinimum : undefined,
      typeKey: spec.typeKey,
      amountKey: spec.amountKey,
      minimumKey: spec.minimumKey,
      typeOptions: withNotDisclosed(spec.typeOptions),
      entryLimits: spec.entryLimits,
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};

const buildChildAgeBandsResult = (
  params: ICellEditorParams,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
): CellEditorOverrideResult => {
  const initialBands = parseMaybeJsonArray(
    resolveInitialFieldValue(params),
  ) as ChildAgeBand[];

  return {
    component:
      ChildBenefitAgeBandsCellEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      initialBands,
      typeOptions: VOLUNTARY_LIFE_DEPENDENT_BENEFIT_OPTIONS,
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};

interface CellSpecLike {
  cellEditor?: string;
  cellEditorParams?: {
    values?: string[];
    formatValue?: (v: string) => string;
    min?: number;
    max?: number;
    step?: number;
    precision?: number;
    [key: string]: unknown;
  };
  suffix?: string;
}

interface BenefitLike {
  label?: string;
  cell?: CellSpecLike;
  properties?: Record<string, { cell?: CellSpecLike }>;
  cellVariants?: {
    dependsOn: string;
    options: Record<string, CellSpecLike>;
    default: CellSpecLike;
  };
}

const resolveCellSpec = (params: ICellEditorParams): CellSpecLike => {
  const data = params.data as
    | {
        benefit?: BenefitLike;
        propertyKey?: string;
      }
    | undefined;
  const benefit = data?.benefit;

  const baseCell =
    data?.propertyKey && benefit?.properties?.[data.propertyKey]?.cell
      ? (benefit.properties[data.propertyKey].cell ?? {})
      : (benefit?.cell ?? {});

  const cellVariants = benefit?.cellVariants;
  if (!cellVariants) {
    return baseCell;
  }

  const dependentValue = getCellValueInCurrentColumn(
    params,
    cellVariants.dependsOn,
  );

  if (
    typeof dependentValue === "string" &&
    cellVariants.options[dependentValue]
  ) {
    return cellVariants.options[dependentValue];
  }

  return cellVariants.default ?? baseCell;
};

const inferValueType = (cell: CellSpecLike): FieldValueType | undefined => {
  const editor = cell.cellEditor;
  // Skip custom editors so we don't shadow specialized popovers
  // (e.g. PopoverListEditor for array fields). Only handle the ag*
  // built-ins and the no-editor case.
  if (editor && !editor.startsWith("ag")) return undefined;

  const params = cell.cellEditorParams ?? {};
  if (Array.isArray(params.values) && params.values.length > 0) return "enum";
  if (
    editor === "agNumberCellEditor" ||
    typeof params.min === "number" ||
    typeof params.max === "number" ||
    typeof params.step === "number" ||
    typeof params.precision === "number"
  ) {
    return "number";
  }
  return "text";
};

const buildTypeHint = (
  valueType: FieldValueType,
  editorParams: NonNullable<CellSpecLike["cellEditorParams"]>,
  cell: CellSpecLike,
  options: FieldPopoverEditorOption[] | undefined,
): string => {
  if (valueType === "enum" && options) {
    return `Enum · ${options.length} option${options.length === 1 ? "" : "s"}`;
  }
  if (valueType === "number") {
    const bits: string[] = ["Number"];
    if (typeof editorParams.min === "number")
      bits.push(`min ${editorParams.min}`);
    if (typeof editorParams.max === "number")
      bits.push(`max ${editorParams.max}`);
    if (cell.suffix) bits.push(cell.suffix);
    return bits.join(" · ");
  }
  return "Text";
};

// The longest prefix shared by every key, e.g. "employeeBenefitMaximum" for
// ["employeeBenefitMaximumDollarAmount", "employeeBenefitMaximumSalaryMultiplier"].
const longestCommonPrefix = (keys: readonly string[]): string => {
  if (keys.length === 0) return "";
  return keys.reduce((prefix, key) => {
    let end = 0;
    while (
      end < prefix.length &&
      end < key.length &&
      prefix[end] === key[end]
    ) {
      end += 1;
    }
    return prefix.slice(0, end);
  }, keys[0]);
};

// The PascalCase suffix each numeric sibling key adds on top of the prefix
// shared by all of them, e.g. `employeeBenefitMaximumSalaryMultiplier` minus
// the shared `employeeBenefitMaximum` prefix leaves "SalaryMultiplier", which
// lowercases to the type value "salaryMultiplier". Used to tell apart a
// field's several type-dependent amount siblings (CD-2568).
const amountKeyTypeSuffix = (
  key: string,
  sharedPrefix: string,
): string | undefined => {
  const suffix = key.slice(sharedPrefix.length);
  if (!suffix) return undefined;
  return suffix.charAt(0).toLowerCase() + suffix.slice(1);
};

const resolveCollapsedAmount = (
  params: ICellEditorParams,
): {
  amountKey?: string;
  initialAmount?: number | null;
  amountKeysByType?: Record<string, string>;
  amountValuesByKey?: Record<string, number | null>;
} => {
  const data = params.data as
    | { [COLLAPSED_SIBLINGS_KEY]?: Record<string, Record<string, unknown>> }
    | undefined;
  const collapsedSiblings = data?.[COLLAPSED_SIBLINGS_KEY];
  const columnField = params.colDef?.field;
  if (!collapsedSiblings || !columnField) return {};

  // Skip non-empty string siblings (e.g. preExistingConditionMonthsDetail) —
  // they are display-only values preserved for the formatter and saved as-is
  // by onCellValueChanged. Treating them as numeric amount inputs overwrites
  // the string with null on every edit. An empty string is a numeric amount
  // that was never set (not a display-only string), so it falls through to
  // the numeric branch below, same as a genuinely unset value (CD-2552).
  const amountValuesByKey: Record<string, number | null> = {};
  for (const key of Object.keys(collapsedSiblings)) {
    const raw = collapsedSiblings[key][columnField];
    if (typeof raw === "string" && raw !== "") continue;
    amountValuesByKey[key] = typeof raw === "number" ? raw : null;
  }

  const numericKeys = Object.keys(amountValuesByKey);
  if (numericKeys.length === 0) return {};

  // A single amount sibling (the overwhelming majority of fields): unchanged
  // behavior from before this key was ever plural.
  if (numericKeys.length === 1) {
    const [amountKey] = numericKeys;
    return { amountKey, initialAmount: amountValuesByKey[amountKey] };
  }

  // Several amount siblings mean this field's type value picks between them
  // (e.g. dollarAmount vs salaryMultiplier) rather than there being one
  // amount slot shared by every type. Map each candidate back to the type
  // value its name encodes, so the editor can write to the sibling that
  // actually matches whichever type is selected (CD-2568) instead of always
  // the first one configured in SIBLING_COLLAPSE_BY_COVERAGE.
  const sharedPrefix = longestCommonPrefix(numericKeys);
  const amountKeysByType: Record<string, string> = {};
  for (const key of numericKeys) {
    const typeValue = amountKeyTypeSuffix(key, sharedPrefix);
    if (typeValue) amountKeysByType[typeValue] = key;
  }

  const initialFieldValue = resolveInitialFieldValue(params);
  const initialKey =
    (typeof initialFieldValue === "string" &&
      amountKeysByType[initialFieldValue]) ||
    numericKeys[0];

  return {
    amountKey: initialKey,
    initialAmount: amountValuesByKey[initialKey],
    amountKeysByType,
    amountValuesByKey,
  };
};

const resolveInitialFieldValue = (params: ICellEditorParams): unknown => {
  const columnField = params.colDef?.field;
  const data = params.data as
    | ({ benefit?: { networkDependent?: boolean } } & Record<string, unknown>)
    | undefined;

  if (columnField && data) {
    const valuePath =
      data.benefit?.networkDependent === true || !columnField.includes(".")
        ? columnField
        : columnField.split(".")[0];
    const currentRowValue = get(data, valuePath) as unknown;
    if (currentRowValue !== undefined) {
      return currentRowValue;
    }
  }

  const benefitKey = (params.data as { benefitKey?: string } | undefined)
    ?.benefitKey;
  if (!benefitKey) return params.value as unknown;

  const currentColumnValue = getCellValueInCurrentColumn(params, benefitKey);
  return currentColumnValue === undefined
    ? (params.value as unknown)
    : currentColumnValue;
};

const buildGroupedEnumResult = (
  params: ICellEditorParams,
  spec: GroupedEnumSpec,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
): CellEditorOverrideResult => {
  const columnField = params.colDef?.field;
  const collapsedSiblings = (
    params.data as
      | { [COLLAPSED_SIBLINGS_KEY]?: Record<string, Record<string, unknown>> }
      | undefined
  )?.[COLLAPSED_SIBLINGS_KEY];

  const readFieldValue = (key: string): string | undefined => {
    if (key === spec.parentKey) {
      const raw = resolveInitialFieldValue(params);
      return typeof raw === "string" ? raw : undefined;
    }
    const raw = columnField
      ? collapsedSiblings?.[key]?.[columnField]
      : undefined;
    return typeof raw === "string" ? raw : undefined;
  };

  // All grouped fields share one cell spec (the parent row's own — e.g.
  // Vision's examFrequency/lensesFrequency/framesFrequency are all built from
  // the same VisionBenefitFrequency enum), so the options list is resolved
  // from it rather than duplicated per field.
  const cell = resolveCellSpec(params);
  const editorParams = cell.cellEditorParams ?? {};
  const options: FieldPopoverEditorOption[] = withNotDisclosed(
    (editorParams.values ?? []).map((value) => ({
      value,
      label: editorParams.formatValue ? editorParams.formatValue(value) : value,
    })),
  );

  return {
    component: GroupedEnumCellEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      fields: spec.fields.map((field) => ({
        key: field.key,
        label: field.label,
        initialValue: readFieldValue(field.key),
      })),
      options,
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};

const buildServiceWaitingPeriodsResult = (
  params: ICellEditorParams,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
): CellEditorOverrideResult => {
  const rawValue = resolveInitialFieldValue(params);
  const initialPeriods: ServiceWaitingPeriod[] = Array.isArray(rawValue)
    ? (rawValue as ServiceWaitingPeriod[])
    : [];
  const fieldLabel =
    (params.data as { benefit?: { label?: string } } | undefined)?.benefit
      ?.label ??
    params.colDef?.headerName ??
    "Value";

  return {
    component:
      ServiceWaitingPeriodsCellEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      initialPeriods,
      fieldLabel,
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};

const buildGenericFieldResult = (
  params: ICellEditorParams,
  customTextOptions: QuotesPopupCustomTextOptions | undefined,
  notOfferedAllowed: ReadonlySet<string>,
): CellEditorOverrideResult | undefined => {
  const cell = resolveCellSpec(params);
  const valueType = inferValueType(cell);
  if (!valueType) return undefined;

  const editorParams = cell.cellEditorParams ?? {};
  const fieldLabel =
    (params.data as { benefit?: { label?: string } } | undefined)?.benefit
      ?.label ??
    params.colDef?.headerName ??
    "Value";

  const benefitKey = (params.data as { benefitKey?: string } | undefined)
    ?.benefitKey;

  const rawOptions: FieldPopoverEditorOption[] = (
    editorParams.values ?? []
  ).map((value) => ({
    value,
    label: editorParams.formatValue ? editorParams.formatValue(value) : value,
  }));

  const baseOptions: FieldPopoverEditorOption[] | undefined =
    valueType === "enum" ? withNotDisclosed(rawOptions) : undefined;

  const options: FieldPopoverEditorOption[] | undefined =
    baseOptions && !!benefitKey && notOfferedAllowed.has(benefitKey)
      ? withNotOffered(baseOptions)
      : baseOptions;

  const typeHint = buildTypeHint(valueType, editorParams, cell, options);
  const { amountKey, initialAmount, amountKeysByType, amountValuesByKey } =
    resolveCollapsedAmount(params);

  return {
    component: FieldPopoverEditor as unknown as ComponentType<unknown>,
    popup: true,
    params: {
      initialFieldValue: resolveInitialFieldValue(params),
      valueType,
      fieldLabel,
      typeHint,
      options,
      min: editorParams.min,
      max: editorParams.max,
      step: editorParams.step,
      integer: editorParams.precision === 0,
      amountKey,
      initialAmount,
      amountKeysByType,
      amountValuesByKey,
      ...buildCustomTextEditorParams(params, customTextOptions),
    },
  };
};
